import { useState } from 'react'
import { api } from '@/api'
import type { VideoAnalysis } from '@/api/types'
import { liveVideoAnalysis } from '@/api/types'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { Icon } from './icons'
import { Badge, Button, Card, CardBody, CardHead, Empty, Field, Input } from './ui'

/**
 * 「镜头结构」卡 —— 视频复刻第 1 页那颗「分析视频」的全部界面。
 *
 * ## 它为什么单独成卡、而不是塞进上传框下面一行
 *
 * 用户口径（第二轮）：「上传后应该有分析按钮啊，都说第一部分是分析视频」。
 * 传完片子之后他真正要回答的第一个问题是**「这条片子是怎么拍的」**——
 * 后面几页（引擎 / 参数 / 内容）全都围着这个答案转。所以这一步是一次
 * **独立的产出**：要等十几秒、要花钱（¥0.30/次）、不满意会重跑，跟「改一行参数」
 * 不是一个量级的东西，给它一块自己的位置。
 *
 * ## 与 `/prompt/refine`、`AiFillButton` 同族，四条约定一致
 *
 * ① **不建 job** —— 它改的是**输入**（给后面用的参考），不是交付物；
 * ② **成功之后必须刷一次余额** —— 后端真扣了积分，不刷的话顶栏那个数字不动，
 *    用户以为没花钱而账本上已经记了一笔。**刷新放在拿到响应之后、不在成功分支里**，
 *    因为记账排在后端解析之前：解析失败这笔钱也花了。
 * ③ **空结果必须报错** —— 「花了钱 + 提示成功 + 页面没变化」是最坏的失败形态；
 * ④ **额度提示用 `coins`（积分）不用 `cost`（元）** —— 顶栏是积分口径，说元对不上账。
 *
 * ## 🔴 不能只看 HTTP 状态就报成功
 *
 * 上游对视频有**假成功**的路子：`gemini-3.7-flash` 收到 `video_url` 会把内容块
 * 静默丢掉、回一个 200，正文写「您似乎还没有上传视频」。所以判据是后端回的
 * `parsed` —— false 时**把原文摆出来**，绝不显示「分析成功 + 空清单」。
 */
export function VideoAnalysisCard({
  videoUrl,
  analysis,
  onChange,
  sourcePage,
}: {
  /** 当前挂着的参考视频（**公网绝对地址**）。空串 = 还没传 */
  videoUrl: string
  /** 草稿里存着的那一份（**可能是过期的**，本卡自己判）。null = 没分析过 */
  analysis: VideoAnalysis | null
  onChange: (next: VideoAnalysis | null) => void
  /** 出处，只用于用量账本 */
  sourcePage?: string
}) {
  const [busy, setBusy] = useState(false)
  /**
   * 上一次「没解析出来」时模型给的原文（只在组件内留着）。
   *
   * 为什么不落草稿：这份东西没用（它没被解析成结构、也进不了提示词），
   * 落进草稿只会在下次进来时显示一段来路不明的文字。但**当次必须看得见** ——
   * 用户花了三毛、界面什么也不给，等于让他去猜。
   */
  const [rawFallback, setRawFallback] = useState('')
  const toast = useToast()
  const { refresh: refreshBalance } = useAuth()

  /**
   * 这份结构是不是**按上一条片子**分析的。
   *
   * 为什么必须判：不判的话，换一条新视频后旧结构还照样进提示词 ——
   * 出片出来才发现复刻的是上一条的镜头，而钱已经花了。
   *
   * ⚠️ 判据**不在本文件里**：`liveVideoAnalysis()`（`api/types.ts`）才是唯一实现，
   * 因为拼提示词的 `buildVideoPrompt` 也要用同一条判据。两处各写一遍的话，
   * 有一处把 `!==` 写成 `===`，症状是「界面显示正常、成片按上一条片子拍」。
   */
  const live = liveVideoAnalysis(analysis, videoUrl)
  const stale = Boolean(analysis) && !live
  /** 清单里去掉空行之后的条数 —— 显示与提示词都以它为准 */
  const liveShots = live ? live.shots.filter((s) => s.trim()) : []

  const run = async () => {
    if (busy) return
    if (!videoUrl) {
      toast.error('先上传一条参考视频 —— 分析要基于它。')
      return
    }
    setBusy(true)
    try {
      const res = await api.analyzeVideo({ videoUrl, sourcePage })
      /*
       * 拿到响应就刷余额 —— **不能放进下面的成功分支**：后端记账排在解析之前，
       * 所以「没解析出来」那一支同样真的花了钱。放进成功分支的话，用户会看到
       * 「提示失败 + 余额不动」，然后再点一次。
       */
      void refreshBalance()
      const coinNote = res.coins > 0 ? ` · 扣 ${res.coins} 积分` : ''
      const spent = `¥${res.cost.toFixed(2)}${coinNote} · ${(res.elapsedMs / 1000).toFixed(1)}s`

      if (!res.parsed) {
        /* 假成功（见文件头注释）——把原文摆出来，别显示「成功 + 空清单」 */
        setRawFallback(res.raw || '（上游返回了空正文）')
        toast.error(`模型没按格式返回镜头清单（${res.model} · ${spent}）。已把原文放在下面。`)
        return
      }

      setRawFallback('')
      onChange({
        videoUrl,
        shotCount: res.shots.length,
        rhythm: res.rhythm,
        shots: res.shots,
        style: res.style,
      })
      toast.toast(
        `已拆出 ${res.shots.length} 个镜头（${res.model} · ${spent}）。下面可以直接改。`,
        'success',
      )
    } catch (e) {
      // 如实报错，且带出上游原话：Key 失效 / 视频拉不到（超时）/ 被拒 三种处理方式不同
      toast.error(`分析视频失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  /* 下面三个改动都要求有 `live` —— 过期的那份正在被替换，不该还能编辑 */

  const changeShot = (i: number, text: string) => {
    if (!live) return
    const shots = live.shots.map((s, idx) => (idx === i ? text : s))
    onChange({ ...live, shots, shotCount: shots.filter((s) => s.trim()).length })
  }

  const removeShot = (i: number) => {
    if (!live) return
    const shots = live.shots.filter((_, idx) => idx !== i)
    onChange({ ...live, shots, shotCount: shots.filter((s) => s.trim()).length })
  }

  const addShot = () => {
    if (!live) return
    onChange({ ...live, shots: [...live.shots, ''], shotCount: live.shotCount })
  }

  return (
    <Card className="vanalysis">
      <CardHead
        title="镜头结构"
        icon="sparkles"
        right={live ? <Badge tone="primary">{liveShots.length} 个镜头</Badge> : undefined}
      />
      <CardBody>
        {/* ---------- 还没传片子：这一步的前置不成立 ---------- */}
        {!videoUrl ? (
          <Empty
            icon="video"
            title="先上传参考视频"
            text="分析要基于那条片子 —— 传完再回来点「分析视频」。"
          />
        ) : null}

        {/* ---------- 传了、但（还没有 或 已过期）结构 ---------- */}
        {videoUrl && !live ? (
          <>
            {stale ? (
              <div className="vanalysis__stale">
                <Icon name="refresh" size={14} />
                <span>
                  这份结构是按<b>上一条视频</b>分析的，跟现在挂着的这条对不上 ——
                  它不会进提示词。重新分析一下现在这条。
                </span>
              </div>
            ) : null}
            {rawFallback ? (
              /* 上一次没解析出来：原文摆在这里（花了钱就得让用户看见换回了什么） */
              <div className="vanalysis__raw">
                <div className="vanalysis__raw-head">
                  <Icon name="alert" size={13} />
                  模型返回的原文（没能拆成清单）
                </div>
                <pre className="vanalysis__raw-body">{rawFallback}</pre>
              </div>
            ) : null}
            <div className="vanalysis__cta">
              <Button icon="sparkles" loading={busy} data-analyze="run" onClick={run}>
                {busy ? '正在分析这条片子…' : stale ? '重新分析' : '分析视频'}
              </Button>
              <span className="vanalysis__note">
                AI 要先把整条片子拉过去读一遍，通常十几秒。
                <b>一次 ¥0.30（3 积分）</b>，按次计费、不建任务。
              </span>
            </div>
          </>
        ) : null}

        {/* ---------- 有生效的结构：可编辑清单 ---------- */}
        {live ? (
          <>
            <div className="vanalysis__head">
              <span className="vanalysis__head-text">
                下面这些会<b>逐条</b>进提示词 —— 哪一条不对就直接改，删掉的就跟着消失。
              </span>
              <Button
                size="sm"
                variant="ghost"
                icon="refresh"
                loading={busy}
                data-analyze="rerun"
                onClick={run}
              >
                重新分析
              </Button>
            </div>

            <Field label="整体节奏">
              <Input
                value={live.rhythm}
                placeholder="如：快，平均每个镜头 1.5 秒"
                onChange={(e) => onChange({ ...live, rhythm: e.target.value })}
              />
            </Field>

            <Field label="镜头清单" hint="一条镜头一行 —— 写的是这条片子里的画面，复刻时按这个结构换产品">
              <div className="vanalysis__rows">
                {live.shots.map((s, i) => (
                  <div key={i} className="vanalysis__row" data-shot={i}>
                    <span className="vanalysis__idx">{i + 1}</span>
                    <Input
                      value={s}
                      placeholder={`第 ${i + 1} 个镜头（空着 = 不要这一条）`}
                      onChange={(e) => changeShot(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="vanalysis__del"
                      title="删掉这个镜头"
                      aria-label="删掉这个镜头"
                      onClick={() => removeShot(i)}
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" variant="ghost" icon="plus" onClick={addShot}>
                  加一个镜头
                </Button>
              </div>
            </Field>

            <Field label="画面风格">
              <Input
                value={live.style}
                placeholder="如：柔光、浅色背景、室内场景"
                onChange={(e) => onChange({ ...live, style: e.target.value })}
              />
            </Field>
          </>
        ) : null}
      </CardBody>
    </Card>
  )
}
